<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import ProportionBar from '../analytics/ProportionBar.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { QUEM_CORES } from '../atendimento/quemCores';
import { campanhasLabel } from './campanhasLabels';

// O funil de sete etapas, de enviadas a resolvidas. A unidade é o destinatário
// (G5); cada etapa é parte da anterior, e o servidor já manda os percentuais
// sobre as enviadas e sobre a etapa de antes. A contagem de conversas é o número
// secundário: um contato pode receber mais de um envio na mesma conversa.
// Enviada é o que a Meta confirmou: o envio sem confirmação não entra aqui.
const props = defineProps({
  // A resposta de GET campanhas/resumo; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const { t } = useI18n();
const { number, percent } = useAnalyticsFormat();

const FILLS = {
  sent: QUEM_CORES.disparo.fill,
  delivered: QUEM_CORES.disparo.fill,
  read: QUEM_CORES.disparo.fill,
  replied: QUEM_CORES.disparo.fill,
  ai_attended: QUEM_CORES.ia.fill,
  handed_to_person: QUEM_CORES.pessoa.fill,
  resolved: 'fill-n-teal-9',
};

const conversations = computed(() => props.report?.conversations || null);
const sent = computed(
  () => props.report?.funnel?.find(step => step.step === 'sent')?.count || 0
);

// O número secundário das conversas: nas enviadas e nas que responderam.
const secondary = step => {
  if (!conversations.value) return '';
  if (step === 'sent')
    return t(
      'CAMPANHAS.FUNNEL.CONVERSATIONS',
      { count: number(conversations.value.total) },
      conversations.value.total
    );
  if (step === 'replied')
    return t(
      'CAMPANHAS.FUNNEL.CONVERSATIONS',
      { count: number(conversations.value.replied) },
      conversations.value.replied
    );
  return '';
};

const steps = computed(() =>
  (props.report?.funnel || []).map((step, index) => ({
    ...step,
    label: campanhasLabel(t, 'step', step.step),
    fill: FILLS[step.step] || 'fill-n-slate-6',
    secondary: secondary(step.step),
    // A primeira etapa é o todo: o "da anterior" só a partir da segunda.
    previous: index > 0 ? step.percent_of_previous : null,
  }))
);
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanhas-funil"
    :title="t('CAMPANHAS.FUNNEL.TITLE')"
    :description="t('CAMPANHAS.FUNNEL.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <p
      v-else-if="report && !sent"
      data-test-id="campanhas-funil-vazio"
      class="mb-0 text-sm text-n-slate-11"
    >
      {{ t('CAMPANHAS.FUNNEL.EMPTY') }}
    </p>
    <ol v-else-if="report" class="flex flex-col gap-1 m-0 list-none">
      <li
        v-for="step in steps"
        :key="step.step"
        :data-test-id="`campanhas-funil-${step.step}`"
        class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center py-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(6rem,14rem)_7rem]"
      >
        <span class="flex flex-col min-w-0">
          <span class="text-sm break-words text-n-slate-12">
            {{ step.label }}
          </span>
          <span
            v-if="step.secondary"
            :data-test-id="`campanhas-funil-${step.step}-conversas`"
            class="text-xs text-n-slate-10"
          >
            {{ step.secondary }}
          </span>
        </span>
        <ProportionBar
          class="order-last col-span-2 h-2 sm:order-none sm:col-span-1"
          :segments="[{ key: step.step, value: step.count, fill: step.fill }]"
          :total="sent"
        />
        <span class="text-sm whitespace-nowrap text-end tabular-nums">
          <span class="font-medium text-n-slate-12">
            {{ number(step.count) }}
          </span>
          <span
            v-if="typeof step.percent_of_sent === 'number'"
            class="ms-1.5 text-xs text-n-slate-11"
          >
            {{ percent(step.percent_of_sent) }}
          </span>
          <span
            v-if="typeof step.previous === 'number'"
            :data-test-id="`campanhas-funil-${step.step}-anterior`"
            class="block text-xs text-n-slate-10"
          >
            {{
              t('CAMPANHAS.FUNNEL.OF_PREVIOUS', {
                percent: percent(step.previous),
              })
            }}
          </span>
        </span>
      </li>
    </ol>
  </AnalyticsPanel>
</template>
